import React, { useCallback, useRef, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { object, string, ref as yupRef } from "yup";
import { getErrorMessageFromApiError } from "@fmc-digital/assets";
import type { ResetPassworRequestBodyBody } from "@fmc-digital/auth-api-generated";
import { useResetPasswordPost } from "@fmc-digital/auth-api-generated";
import { getCurrentLanguage, useTranslation } from "@fmc-digital/i18n";
import { Button, cn, FormHeader } from "@fmc-digital/ui";
import { ROUTES } from "@/constants/routes.constants";
import { useRecoverUsername } from "@/contexts/RecoverUsernameContext";
import { trackForgotPasswordEvent, ForgotPasswordEvent } from "@/utils/analytics.utils";
import { ResetPasswordFields, type FormValues } from "@/components/molecules/ResetPasswordFields";
import { PasswordRequirementList, getPasswordRequirements } from "@/components/molecules/PasswordRequirementList";
import {
  resetPasswordFormStyles,
  resetPasswordFormContentStyles,
  resetPasswordFormButtonGroupStyles,
  resetPasswordFormErrorStyles,
} from "./reset-password-form.styles";
import { FormErrorBanner } from "@/components/molecules/FormErrorBanner";

/**
 * Builds Yup validation schema from password requirements to ensure consistency
 * between form validation and UI requirement checks.
 * Uses the validators directly to avoid duplication and potential inconsistencies.
 */
const buildValidationSchema = (email?: string, lastName?: string) => {
  let passwordSchema = string().required("New password is required");

  // Build password validation from requirements validators directly
  // This ensures the schema always matches the UI requirement checks
  const requirements = getPasswordRequirements(email, lastName);
  requirements.forEach((requirement) => {
    passwordSchema = passwordSchema.test(
      requirement.id,
      requirement.label,
      (value) => {
        if (!value) return true; // Let required() handle empty values
        return requirement.validator(value);
      }
    );
  });

  return object().shape({
    newPassword: passwordSchema,
    confirmPassword: string()
      .required("Please confirm your password")
      .oneOf([yupRef("newPassword")], "Passwords must match"),
  });
};

/**
 * ResetPasswordForm organism component
 * Combines FormHeader, ResetPasswordFields, and PasswordRequirementList
 * Handles form validation, API calls, and state management
 * Uses RecoverUsernameContext for guid
 */
export const ResetPasswordForm: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { guid, email, lastName } = useRecoverUsername();
  const { t } = useTranslation();

  // Reset password mutation hook
  const {
    mutate: resetPassword,
    isPending: isLoading,
    error,
  } = useResetPasswordPost();

  // Capture initial email and lastName values to prevent schema rebuild during form lifecycle
  // This ensures the validation schema remains stable and doesn't reset form state unexpectedly
  const initialEmailRef = useRef<string | undefined>(email ?? undefined);
  const initialLastNameRef = useRef<string | undefined>(lastName ?? undefined);

  // Build validation schema once with initial values to prevent form state resets
  // The schema should not change during the form lifecycle even if context values update
  const validationSchema = React.useMemo(
    () => buildValidationSchema(initialEmailRef.current, initialLastNameRef.current),
    [] // Empty dependency array - schema is built once and remains stable
  );

  const form = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { newPassword: "", confirmPassword: "" },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!guid) {
        console.error("GUID is missing. Cannot reset password.");
        return;
      }

      // Validate password is not empty or undefined before encoding
      // This is a defensive check in case form validation is bypassed
      if (!values.newPassword || values.newPassword.trim().length === 0) {
        console.error("New password is empty or undefined. Cannot reset password.");
        return;
      }

      // Prepare request body
      // Use Unicode-safe Base64 encoding to handle emojis, accented characters, etc.
      // btoa() only supports Latin1, so we need to convert Unicode to UTF-8 first
      // Using TextEncoder instead of deprecated unescape(encodeURIComponent())
      const utf8Bytes = new TextEncoder().encode(values.newPassword);
      const binaryString = String.fromCharCode(...utf8Bytes);
      const base64Password = btoa(binaryString);
      const requestBody: ResetPassworRequestBodyBody = {
        newPassword: base64Password,
      };

      // Call the reset password API
      resetPassword(
        { data: requestBody },
        {
          onSuccess: () => {
            console.log("Password reset successful");
            
            // Track successful password reset
            trackForgotPasswordEvent(ForgotPasswordEvent.RESET_PWD_SUCCESS);
            
            // Navigate to login with success flag and email for field prepopulation (same pattern as forgot-email)
            navigate(ROUTES.LOGIN, {
              state: {
                passwordResetSuccess: true,
                ...(email ? { username: email } : {}),
              },
            });
          },
          onError: (err) => {
            console.error("Reset password error:", err);
          },
        },
      );
    },
    [guid, resetPassword, navigate, email],
  );

  const handleCancel = useCallback(() => {
    form.reset();
    navigate(ROUTES.LOGIN);
  }, [form, navigate]);

  // Format error message based on error code
  const getErrorMessage = useCallback((error: unknown): string => {
    try {
      const lang = getCurrentLanguage() ?? "en";
      return getErrorMessageFromApiError(error as never, lang);
    } catch {
      // Fallback if i18n is not initialized
      return getErrorMessageFromApiError(error as never, "en");
    }
  }, []);

  const newPassword = form.watch("newPassword");

  // Form validity is determined by Yup schema validation only
  // The schema validates all password requirements and password matching,
  // so we rely solely on form.formState.isValid to avoid duplication
  const isFormValid = form.formState.isValid && !isLoading;

  const buttonProps = {
    update: {
      type: "submit" as const,
      variant: "filled" as const,
      size: "large" as const,
      loading: isLoading,
      disabled: !isFormValid,
    },
    cancel: {
      type: "button" as const,
      variant: "outlined" as const,
      size: "large" as const,
      onClick: handleCancel,
    },
  };

  const handleFormSubmit = useCallback((e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    form.handleSubmit(onSubmit)(e);
  }, [form, onSubmit]);

  // Focus and scroll error banner from parent when error changes
  
    useEffect(() => {
      if (error) {
        const el = document.querySelector('[data-testid="reset-password-form-error"]');
        if (el instanceof HTMLElement) {
          el.focus();
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }, [error]);
  return (
    <div
      className={cn(resetPasswordFormStyles())}
      data-testid="reset-password-form"
    >
      {/* Header - responsive styling handled by FormHeader component */}
      <FormHeader
        title={t("auth.resetPassword.title")}
        testId="reset-password-header"
      />

      {/* Content wrapper - card appears on tablet+ via CSS */}
      <div className={cn(resetPasswordFormContentStyles())}>
        {/* Form fields with buttons for tablet/desktop (inside card) */}
        <ResetPasswordFields form={form} onSubmit={handleFormSubmit}>
          {/* Password Requirements */}
          <PasswordRequirementList
            password={newPassword}
            email={email ?? undefined}
            lastName={lastName ?? undefined}
            testId="reset-password-requirements"
          />

          {/* Buttons for tablet/desktop (inside form, inside card) */}
          {/* Tablet: Update Password first, Cancel second | Desktop: Cancel first, Update Password second */}
          <div
            className={cn(
              resetPasswordFormButtonGroupStyles(),
              "hidden md:flex",
            )}
          >
            <Button {...buttonProps.update} className="flex-1">
              {t("auth.resetPassword.buttons.update")}
            </Button>
            <Button {...buttonProps.cancel} className="flex-1">
              {t("auth.resetPassword.buttons.cancel")}
            </Button>
          </div>
          <div
            className={cn(resetPasswordFormButtonGroupStyles(), "md:hidden")}
          >
            <Button {...buttonProps.update} className="w-full">
              {t("auth.resetPassword.buttons.update")}
            </Button>
            <Button {...buttonProps.cancel} className="w-full">
              {t("auth.resetPassword.buttons.cancel")}
            </Button>
          </div>
        </ResetPasswordFields>
      </div>

      <FormErrorBanner
        error={error}
        getErrorMessage={getErrorMessage}
        className={cn(resetPasswordFormErrorStyles())}
        testId="reset-password-form-error"
      />
    </div>
  );
};
